import { useEffect, useState, useCallback } from 'react';
import { $getRoot, $createParagraphNode, $createTextNode, $getSelection, $isRangeSelection } from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { LinkNode, TOGGLE_LINK_COMMAND, $isLinkNode } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { LinkIcon, LinkOffIcon } from './SVGIcons';

interface NotificationLexicalEditorProps {
  value: string;
  onChange: (value: string) => void;
}

// ツールバーコンポーネント
function Toolbar() {
  const [editor] = useLexicalComposerContext();
  const [isLinkEditMode, setIsLinkEditMode] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [isLink, setIsLink] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setSelectedText(selection.getTextContent());
      
      // 選択範囲がリンクかどうかをチェック
      const node = selection.anchor.getNode();
      const parent = node.getParent();
      setIsLink($isLinkNode(parent) || $isLinkNode(node));
    }
  }, []);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const insertLink = () => {
    if (!linkUrl) return;

    editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
    setIsLinkEditMode(false);
    setLinkUrl('');
  };

  const handleLinkButtonClick = () => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (isLink) {
          // リンク解除（選択不要）
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        } else {
          // リンク追加は選択が必要
          if (!selection.isCollapsed()) {
            setIsLinkEditMode(true);
          } else {
            alert('リンクを追加するテキストを選択してください');
          }
        }
      }
    });
  };

  return (
    <div className="mb-2 relative">
      <button
        type="button"
        onClick={handleLinkButtonClick}
        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded flex items-center"
      >
        {isLink ? (
          <>
            <LinkOffIcon />
            <span className="ml-1">リンク解除</span>
          </>
        ) : (
          <>
            <LinkIcon />
            <span className="ml-1">リンク</span>
          </>
        )}
      </button>

      {isLinkEditMode && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => {
            setIsLinkEditMode(false);
            setLinkUrl('');
          }} />
          <div className="absolute top-full left-0 mt-1 z-40">
            <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 w-max">
              <p className="text-sm text-gray-600 mb-2 truncate" style={{ maxWidth: '300px' }}>選択テキスト: {selectedText}</p>
              <div className="flex gap-2 items-center">
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="リンクを貼り付け"
                  className="w-80 px-3 py-2 border border-gray-300 rounded text-sm"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={insertLink}
                  className="px-2 py-1 text-blue-600 hover:text-blue-800 text-sm font-medium whitespace-nowrap"
                >
                  適用
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// エディタプラグインコンポーネント
function EditorPlugins({ value, onChange }: NotificationLexicalEditorProps) {
  const [editor] = useLexicalComposerContext();
  const [isFirstRender, setIsFirstRender] = useState(true);

  // リンククリックハンドラー
  useEffect(() => {
    const handleClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const linkElement = target.closest('a');

      if (linkElement && ((event as MouseEvent).ctrlKey || (event as MouseEvent).metaKey)) {
        event.preventDefault();
        event.stopPropagation();
        const href = linkElement.getAttribute('href');
        if (href) {
          window.open(href, '_blank', 'noopener,noreferrer');
        }
      }
    };

    const handleMouseOver = (event: Event) => {
      const target = event.target as HTMLElement;
      const linkElement = target.closest('a');
      if (linkElement && !linkElement.hasAttribute('title')) {
        linkElement.setAttribute('title', 'Ctrl/Cmd+クリックで開く');
      }
    };

    const editorElement = editor.getRootElement();
    if (editorElement) {
      editorElement.addEventListener('click', handleClick);
      editorElement.addEventListener('mouseover', handleMouseOver);
      return () => {
        editorElement.removeEventListener('click', handleClick);
        editorElement.removeEventListener('mouseover', handleMouseOver);
      };
    }
  }, [editor]);

  // 初期値の設定
  useEffect(() => {
    if (isFirstRender && value) {
      editor.update(() => {
        const root = $getRoot();
        root.clear();

        // プレーンテキストかLexical JSONかを判定
        try {
          const parsed = JSON.parse(value);
          if (parsed.root) {
            // Lexical形式のデータ
            const editorState = editor.parseEditorState(value);
            editor.setEditorState(editorState);
          } else {
            throw new Error('Not Lexical format');
          }
        } catch {
          // プレーンテキストの場合
          const lines = value.split('\n');
          lines.forEach((line, index) => {
            const paragraph = $createParagraphNode();
            if (line) {
              paragraph.append($createTextNode(line));
            }
            root.append(paragraph);
          });
        }
      });
      setIsFirstRender(false);
    }
  }, [editor, value, isFirstRender]);

  const handleChange = (editorState: any) => {
    editorState.read(() => {
      // Lexical JSONとして保存
      const json = JSON.stringify(editorState.toJSON());
      onChange(json);
    });
  };

  return (
    <>
      <Toolbar />
      <div className="relative">
        <div className="border border-gray-300 rounded-lg bg-gray-50 overflow-hidden resize-y" style={{ minHeight: '120px', maxHeight: '400px', resize: 'vertical' }}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="p-2.5 focus:outline-none overflow-y-auto"
                style={{ minHeight: '100px', height: '100%' }}
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <OnChangePlugin onChange={handleChange} />
          <HistoryPlugin />
          <LinkPlugin />
        </div>
      </div>
    </>
  );
}

export default function NotificationLexicalEditor({ value, onChange }: NotificationLexicalEditorProps) {
  const initialConfig = {
    namespace: 'NotificationEditor',
    theme: {
      link: 'text-blue-600 underline cursor-pointer hover:text-blue-800',
      text: {
        underline: 'underline',
      },
      placeholder: 'text-gray-400',
    },
    nodes: [LinkNode],
    onError: (error: Error) => {
      // エラーハンドリング
    },
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div>
        <EditorPlugins value={value} onChange={onChange} />
      </div>
    </LexicalComposer>
  );
}